/**
 * Cliente API: base URL y headers (token, X-Entity-Id).
 */
export function getApiBase(): string {
  const env = (process.env.REACT_APP_API_URL ?? '').trim();
  if (env) return env;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return `http://${h}:5084`;
    }
  }
  return '';
}

const TOKEN_KEY = 'tesoreria_token';
const ENTITY_KEY = 'tesoreria_entity_id';

let entityId: number | null = null;
const savedEntity = typeof localStorage !== 'undefined' ? localStorage.getItem(ENTITY_KEY) : null;
if (savedEntity) {
  const n = parseInt(savedEntity, 10);
  if (!isNaN(n)) entityId = n;
}

function readTokenFromStorage(): string | null {
  return typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
}

export function setToken(t: string | null) {
  if (typeof localStorage !== 'undefined') {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  }
}

export function getToken(): string | null {
  return readTokenFromStorage();
}

export function setEntityId(id: number | null) {
  entityId = id;
  if (typeof localStorage !== 'undefined') {
    if (id != null) localStorage.setItem(ENTITY_KEY, String(id));
    else localStorage.removeItem(ENTITY_KEY);
  }
}

export function getEntityId(): number | null {
  return entityId;
}

/** Convierte respuestas de error API en mensaje legible (nunca JSON crudo). */
export function parseApiError(text: string, status?: number): string {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    if (status === 401) return 'Sesión expirada. Vuelve a iniciar sesión.';
    if (status === 403) return 'No tienes permiso para esta acción.';
    return status ? `Error del servidor (${status})` : 'Error de conexión';
  }
  try {
    const parsed = JSON.parse(trimmed) as { detail?: string | { msg?: string }[] };
    if (typeof parsed.detail === 'string') return parsed.detail;
    if (Array.isArray(parsed.detail) && parsed.detail[0]?.msg) {
      return parsed.detail.map((d) => d.msg).filter(Boolean).join('. ');
    }
  } catch {
    /* texto plano */
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return status === 401 ? 'Sesión expirada. Vuelve a iniciar sesión.' : 'Error inesperado del servidor';
  }
  return trimmed;
}

function handleUnauthorized(): void {
  setToken(null);
  setEntityId(null);
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('tesoreria_entities');
    localStorage.removeItem('tesoreria_view_all_entities');
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login?session=expired';
  }
}

export function buildAuthHeaders(extra?: HeadersInit, omitContentType?: boolean): HeadersInit {
  const headers: Record<string, string> = { ...(extra as Record<string, string>) };
  if (!omitContentType && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const t = readTokenFromStorage();
  if (t) headers['Authorization'] = `Bearer ${t}`;
  if (entityId != null) headers['X-Entity-Id'] = String(entityId);
  return headers;
}

export async function authorizedFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const omitContentType = options.body instanceof FormData;
  const headers = buildAuthHeaders(options.headers, omitContentType);
  const res = await fetch(`${getApiBase()}${path}`, { ...options, headers });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
  }
  return res;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await authorizedFetch(path, options);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseApiError(text, res.status));
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const apiGet = <T = unknown>(path: string) => api<T>(path, { method: 'GET' });
export const apiPost = <T = unknown>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });
export const apiPatch = <T = unknown>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
export const apiDelete = (path: string) => api(path, { method: 'DELETE' });

export async function apiPostForm<T = unknown>(path: string, formData: FormData): Promise<T> {
  const res = await authorizedFetch(path, { method: 'POST', body: formData });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseApiError(text, res.status));
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
